import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import './styles/animations.css';
import './styles/ElementDisplayNavigation.css';
import alifPlayLogo from './AlifPlay.png';
import categoriesData from './data/categories.json';
import ElementDisplay from './components/ElementDisplay';
import LanguageLabels from './components/LanguageLabels';
import CategoryNavigation from './components/CategoryNavigation';

function App() {
  // Ref for header
  const headerRef = useRef(null);
  // State for header collapse
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  
  // Extract categories from the JSON data
  const categories = categoriesData.categories.map(category => category.id);
  
  // Create a mapping of category IDs to their element IDs
  const categoryElements = React.useMemo(() => {
    const elementsMap = {};
    categoriesData.categories.forEach(category => {
      elementsMap[category.id] = category.elements.map(element => element.id);
    });
    return elementsMap;
  }, []); // Empty dependency array since categoriesData is imported and won't change
  
  // State for active category and element
  const [activeCategory, setActiveCategory] = useState(categories[0]);
  const [activeElement, setActiveElement] = useState(categoryElements[categories[0]][0]);
  const [displayColor, setDisplayColor] = useState('red');
  const [displayMode, setDisplayMode] = useState('all'); // 'all', 'en', 'ar', 'de'
  const [continuousPlay, setContinuousPlay] = useState(false);
  const [playInterval, setPlayInterval] = useState(3000); // 3 seconds by default
  const [autoSpeak, setAutoSpeak] = useState(false);
  const [speechSynthesis, setSpeechSynthesis] = useState(null);
  const [isVoicesLoaded, setIsVoicesLoaded] = useState(false);
  
  // Handle navigation to previous or next element
  const handleElementNavigation = useCallback((direction) => {
    const elementsInCategory = categoryElements[activeCategory] || [];
    const currentIndex = elementsInCategory.indexOf(activeElement);
    
    if (direction === 'prev') {
      if (currentIndex > 0) {
        setActiveElement(elementsInCategory[currentIndex - 1]);
      } else if (elementsInCategory.length > 0) {
        // Wrap around to the last element
        setActiveElement(elementsInCategory[elementsInCategory.length - 1]);
      }
    } else if (direction === 'next') {
      if (currentIndex < elementsInCategory.length - 1) {
        setActiveElement(elementsInCategory[currentIndex + 1]);
      } else if (elementsInCategory.length > 0) {
        // Wrap around to the first element
        setActiveElement(elementsInCategory[0]);
      }
    }
  }, [activeCategory, activeElement, categoryElements]);

  // Load speech synthesis and check for voices
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      const synth = window.speechSynthesis;
      setSpeechSynthesis(synth);
      
      const checkVoices = () => {
        setIsVoicesLoaded(synth.getVoices().length > 0);
      };
      
      checkVoices();
      synth.onvoiceschanged = checkVoices;
      
      return () => {
        synth.onvoiceschanged = null;
      };
    }
  }, []);
  
  // Speak the current element in the selected language(s)
  const speakCurrentElement = useCallback(() => {
    if (!autoSpeak || !isVoicesLoaded || !speechSynthesis) return;
    
    const elementData = getElementData(activeCategory, activeElement);
    if (!elementData || !elementData.name) return;
    
    // Language map for speech synthesis
    const languageMap = {
      'en': 'en-US',
      'ar': 'ar-SA',
      'de': 'de-DE'
    };
    
    // Speak based on display mode
    if (displayMode === 'all') {
      // Speak all enabled languages
      ['en', 'ar', 'de'].forEach(lang => {
        if (elementData.name[lang]) {
          const utterance = new SpeechSynthesisUtterance(elementData.name[lang]);
          utterance.lang = languageMap[lang];
          utterance.rate = lang === 'ar' ? 0.8 : 0.9;
          speechSynthesis.speak(utterance);
        }
      });
    } else if (displayMode !== 'none' && elementData.name[displayMode]) {
      // Speak only the selected language
      const utterance = new SpeechSynthesisUtterance(elementData.name[displayMode]);
      utterance.lang = languageMap[displayMode];
      utterance.rate = displayMode === 'ar' ? 0.8 : 0.9;
      speechSynthesis.speak(utterance);
    }
  }, [activeCategory, activeElement, autoSpeak, displayMode, isVoicesLoaded, speechSynthesis]);
  
  // Update active element when category or categoryElements changes
  useEffect(() => {
    if (categoryElements[activeCategory] && categoryElements[activeCategory].length > 0) {
      setActiveElement(categoryElements[activeCategory][0]);
    }
  }, [activeCategory, categoryElements]);
  
  // Speak when element changes and auto-speak is enabled
  useEffect(() => {
    if (autoSpeak) {
      speakCurrentElement();
    }
  }, [activeElement, autoSpeak, speakCurrentElement]);

  // Continuous play effect
  useEffect(() => {
    let timer;
    if (continuousPlay) {
      timer = setInterval(() => {
        handleElementNavigation('next');
      }, playInterval);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [continuousPlay, playInterval, handleElementNavigation]);
  
  // Handle scroll for collapsing header
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (scrollPosition > 50 && !isHeaderCollapsed) {
        setIsHeaderCollapsed(true);
      } else if (scrollPosition <= 50 && isHeaderCollapsed) {
        setIsHeaderCollapsed(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHeaderCollapsed]);
  
  // Language options
  const languageOptions = [
    { value: 'all', label: 'All Languages' },
    { value: 'en', label: 'English' },
    { value: 'ar', label: 'Arabic' },
    { value: 'de', label: 'German' }
  ];

  // Function to get display name based on active category and element
  const getDisplayName = () => {
    return activeElement;
  };
  
  // Function to get element data from categories.json
  const getElementData = (categoryId, elementId) => {
    const categoryData = categoriesData.categories.find(cat => cat.id === categoryId);
    if (!categoryData) return null;
    
    const elementData = categoryData.elements.find(elem => elem.id === elementId);
    return elementData;
  };
  


  return (
    <div className="App">
      <header ref={headerRef} className={`App-header ${isHeaderCollapsed ? 'collapsed' : ''}`}>
        <img src={alifPlayLogo} alt="AlifPlay Logo" className="app-logo" />
      </header>
      
      <main className="App-main">
        <CategoryNavigation 
          categories={categories}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          elements={categoryElements}
          activeElement={activeElement}
          setActiveElement={setActiveElement}
          className="animate-fadeIn"
        />
        
        {/* Row 1: Main Element Display */}
        <div className="display-area animate-scaleIn">
          <div className="element-display-with-navigation">
            <button 
              className="nav-button prev transition-all hover-scale" 
              onClick={() => handleElementNavigation('prev')}
              aria-label="Previous element"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" />
              </svg>
            </button>
            
            <ElementDisplay 
              type={activeElement} 
              category={activeCategory === 'colors' ? 'color' : activeCategory.slice(0, -1)} 
              color={activeCategory === 'shapes' ? displayColor : undefined} 
              size={200} 
            />
            
            <button 
              className="nav-button next transition-all hover-scale" 
              onClick={() => handleElementNavigation('next')}
              aria-label="Next element"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 6L8.59 7.41L13.17 12L8.59 16.59L10 18L16 12L10 6Z" />
              </svg>
            </button>
          </div>
          
          <LanguageLabels item={getDisplayName()} displayMode={displayMode} />
          
          {activeCategory === 'shapes' && (
            <div className="color-picker animate-fadeIn">
              <h3>Choose Shape Color</h3>
              <div className="options-container">
                {categoryElements.colors.map(colorId => {
                  const colorData = getElementData('colors', colorId);
                  const colorValue = colorData && colorData.value ? colorData.value : colorId;
                  
                  return (
                    <button 
                      key={colorId}
                      className={`option-button ${displayColor === colorId ? 'selected' : ''} animate-scaleIn`}
                      onClick={() => setDisplayColor(colorId)}
                      style={{ backgroundColor: colorValue }}
                      aria-label={`Color ${colorId}`}
                    >
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Row 2: Elements Selection */}
        <div className="elements-row">
          <div className="elements-container">
            <h2>{activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)}</h2>
            <div className="elements-grid">
              {categoryElements[activeCategory].map(element => (
                <button 
                  key={element}
                  className={`element-button ${activeElement === element ? 'selected' : ''}`}
                  onClick={() => setActiveElement(element)}
                  aria-label={element}
                >
                  <ElementDisplay 
                    type={element} 
                    category={activeCategory === 'colors' ? 'color' : activeCategory.slice(0, -1)} 
                    color={activeCategory === 'shapes' ? (activeElement === element ? displayColor : 'gray') : undefined} 
                    size={50} 
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Row 3: Controls */}
        <div className="controls-row">
          <div className="control-section playback-options">
            <h2>Playback Options</h2>
            <div className="options-container">
              <div className="option-group">
                <label className="option-label">
                  <input
                    type="checkbox"
                    checked={continuousPlay}
                    onChange={(e) => setContinuousPlay(e.target.checked)}
                    className="option-checkbox"
                  />
                  Continuous Play
                </label>
                {continuousPlay && (
                  <div className="play-interval-control">
                    <label htmlFor="play-interval">Interval (seconds):</label>
                    <input
                      id="play-interval"
                      type="range"
                      min="1"
                      max="10"
                      step="1"
                      value={playInterval / 1000}
                      onChange={(e) => setPlayInterval(e.target.value * 1000)}
                    />
                    <span>{playInterval / 1000}s</span>
                  </div>
                )}
              </div>
              
              <div className="option-group">
                <label className="option-label">
                  <input
                    type="checkbox"
                    checked={autoSpeak}
                    onChange={(e) => setAutoSpeak(e.target.checked)}
                    className="option-checkbox"
                  />
                  Automatic Speak Out
                </label>
                <div className="option-description">
                  Speaks the item name when changed
                </div>
              </div>
            </div>
          </div>
          
          <div className="control-section language-section">
            <h2>Language Display</h2>
            <div className="options-container">
              {languageOptions.map(option => (
                <button 
                  key={option.value}
                  className={`language-button ${displayMode === option.value ? 'selected' : ''}`}
                  onClick={() => setDisplayMode(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
      
      <footer className="App-footer">
        <p> <b>AlifPlay</b> - Multilingual Learning App for Kids</p> <br /> 
        <p>For Feedback & appreciation, email: <a href="mailto:s@selimsalman.de" className="footer-email">s@selimsalman.de</a></p>
      </footer>
    </div>
  );
}

export default App;
